import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { authService } from '../services/index.js'
import { callService } from '../services/index.js'
import { chatService } from '../services/index.js'
import { fileService } from '../services/index.js'
import { labelService } from '../services/index.js'
import { notificationService } from '../services/index.js'
import { playbookService } from '../services/index.js'
import { profileService } from '../services/index.js'
import { taskService, type TaskListFilters } from '../services/index.js'
import { teamService } from '../services/index.js'
import { groupService } from '../services/index.js'
import {
  coachingCallRecordingsService,
  type CoachingRecordingBucket,
  type ListRecordingsFilters as CoachingCallRecordingListFilters,
} from '../services/coachingCallRecordingsService.js'
import { coachingCallsService, type ListCoachingCallsFilters } from '../services/coachingCallsService.js'
import { doersUniversityService } from '../services/doersUniversityService.js'
import { masterclassVaultService } from '../services/masterclassVaultService.js'
import { foundationsService } from '../services/foundationsService.js'
import { launchChecklistService } from '../services/launchChecklistService.js'
import { launchDayService } from '../services/launchDayService.js'
import { doerProfileService } from '../services/doerProfileService.js'
import { onboardingService } from '../services/onboardingService.js'
import { adminMasterclassVaultService } from '../services/adminMasterclassVaultService.js'
import { foundersLoungeService } from '../services/foundersLoungeService.js'
import { founderService } from '../services/founderService.js'
import { agentReleaseService } from '../services/agentReleaseService.js'
import { loyaltyService } from '../services/loyaltyService.js'
import { contentViewsService } from '../services/contentViewsService.js'
import { surveyService } from '../services/surveyService.js'
import { coachingSessionService } from '../services/coachingSessionService.js'
import { preSessionService } from '../services/preSessionService.js'
// Import via the services index (= '@doneverse/data/services') so test mocks
// of that module intercept the query fns too.
import { workSubmissionService } from '../services/index.js'
import type { ContentViewKind, FileResponse, LabelKind } from '@doneverse/shared'
import { getStepColors } from '../utils/index.js'

// ─── Key prefixes (for broad invalidation) ──────────────────────
export const queryKeys = {
  playbooks: ['playbooks'] as const,
  blueprint: ['blueprint'] as const,
  workspace: ['workspace'] as const,
  assignedProjects: ['assignedProjects'] as const,
  notifications: ['notifications'] as const,
  messages: ['messages'] as const,
  profile: ['profile'] as const,
  teams: ['teams'] as const,
  groups: ['groups'] as const,
  files: ['files'] as const,
  tasks: ['tasks'] as const,
  labels: ['labels'] as const,
  integrations: ['integrations'] as const,
  coachingCallRecordings: ['coachingCallRecordings'] as const,
  coachingCalls: ['coachingCalls'] as const,
  doersUniversity: ['doersUniversity'] as const,
  masterclassVault: ['masterclassVault'] as const,
  foundations: ['foundations'] as const,
  foundersLounge: ['foundersLounge'] as const,
  contentViews: ['contentViews'] as const,
  surveys: ['surveys'] as const,
  coachingSessions: ['coachingSessions'] as const,
  workSubmissions: ['workSubmissions'] as const,
  calls: ['calls'] as const,
  onboarding: ['onboarding'] as const,
  doerProfiles: ['doerProfiles'] as const,
}

/**
 * The live huddle in a conversation, if any.
 *
 * Two jobs:
 *
 *  1. Hydration. Realtime `call.started` / `call.ended` events are best-effort
 *     (at-most-once, no replay), so this REST read is what makes the joinable
 *     pill survive a page reload, a socket reconnect, or an event that simply
 *     never arrived.
 *
 *  2. Ghost-call healing. The server reconciles against RealtimeKit on this
 *     read: if every participant's browser died, RealtimeKit reports no live
 *     session and the API ends the call rather than handing back a pill that
 *     would join you to an empty room. The poll below means a chat that is
 *     already open heals within ~30s instead of waiting for a navigation —
 *     otherwise someone sitting on the thread would stare at a dead "Join"
 *     button indefinitely.
 *
 * The poll only runs while a call is actually live in this conversation, so an
 * idle chat costs nothing.
 */
export const activeCallOptions = (contactId: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.calls, 'active', contactId] as const,
    queryFn: () => callService.getActive(contactId!),
    enabled: !!contactId,
    // Huddle state turns over fast; never serve it from a stale cache.
    staleTime: 0,
    refetchInterval: (query) => (query.state.data?.call ? 30_000 : false),
  })

const SIGNED_ASSET_STALE_TIME = 5 * 60 * 1000
const NOTIFICATION_REFETCH_INTERVAL = 30 * 1000

// ─── Coaching session queries ───────────────────────────────────
/** The doer's own past sessions (newest first) for the history view. */
export const coachingSessionsHistoryOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.coachingSessions, 'history'] as const,
    queryFn: () => coachingSessionService.list(),
  })

/** A doer's own coaching session + SMART actions, for the close screen. */
export const coachingSessionOptions = (id: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.coachingSessions, 'detail', id] as const,
    queryFn: () => coachingSessionService.fetchById(id!),
    enabled: !!id,
  })

/**
 * The doer's prior turns for a session, for re-rendering the chat thread on
 * resume. Empty for a fresh session; populated once a dropped conversation's
 * post-call webhook has landed. Conversation logs arrive after the live stage
 * markers, so an early empty response must be refreshed when the chat remounts.
 */
export const coachingSessionTranscriptOptions = (id: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.coachingSessions, 'transcript', id] as const,
    queryFn: () => coachingSessionService.getTranscript(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })

/** Pre-fill data for the pre-session prep form (tasks done + outputs submitted). */
export const prepSuggestionsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.coachingSessions, 'prepSuggestions'] as const,
    queryFn: () => preSessionService.fetchSuggestions(),
    staleTime: 60 * 1000,
  })

// ─── Work submission queries (ClickUp 86d3g825y) ────────────────
/** The doer's own work submissions for the workspace (newest first) — the "Files" list. */
export const workSubmissionsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.workSubmissions, 'list'] as const,
    queryFn: () => workSubmissionService.list(),
  })

/** All links from the workspace's active submissions — the Files page's "Submitted links" list. */
export const workSubmissionLinksOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.workSubmissions, 'links'] as const,
    queryFn: () => workSubmissionService.listWorkspaceLinks(),
  })

// ─── Playbook queries ───────────────────────────────────────────
export const masterPlaybooksOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.playbooks, 'masters'] as const,
    queryFn: () => playbookService.fetchAllMasterPlaybooks(),
    staleTime: SIGNED_ASSET_STALE_TIME, // Prevents refetches that generate new signed URLs
  })

export const playbookDetailOptions = (slug: string) =>
  queryOptions({
    queryKey: [...queryKeys.playbooks, 'detail', slug] as const,
    queryFn: () => playbookService.fetchPlaybook(slug),
    enabled: !!slug,
    staleTime: SIGNED_ASSET_STALE_TIME, // Prevents detail view remounts from churning thumbnail URLs
  })

export const assignedPlaybooksOptions = (role: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.playbooks, 'assigned'] as const,
    queryFn: () => playbookService.fetchAssignedPlaybooks(),
    enabled: role === 'doer' || role === 'doing_team' || role === 'admin',
    refetchInterval: 60000,
  })

// ─── Blueprint queries ──────────────────────────────────────────
export const blueprintStepsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.blueprint, 'steps'] as const,
    queryFn: () => playbookService.fetchBlueprintSteps(),
    staleTime: Infinity,
  })

export const stepMetaMapOptions = () =>
  queryOptions({
    ...blueprintStepsOptions(),
    select: (blueprintSteps) => {
      const map = new Map<string, { label: string; pillColor: string; pillTextColor: string }>()
      for (const step of blueprintSteps) {
        const colors = getStepColors(step.step_number)
        map.set(step.id, {
          label: step.title,
          pillColor: colors.pillColor,
          pillTextColor: colors.pillTextColor,
        })
      }
      return map
    },
  })

// ─── Workspace queries ──────────────────────────────────────────
export const workspaceDetailedOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'detailed'] as const,
    queryFn: () => playbookService.fetchWorkspaceDetailed(),
    staleTime: SIGNED_ASSET_STALE_TIME, // Prevents refetches that generate new signed URLs
  })

export const workspacePlaybookIdsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'playbook-ids'] as const,
    queryFn: () => playbookService.fetchUserPlaybookIds(),
  })

export const playbookBriefOptions = (briefId: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'brief', briefId] as const,
    queryFn: () => playbookService.fetchPlaybookBrief(briefId!),
    enabled: !!briefId,
  })

export const assignedProjectsDetailedOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.assignedProjects, 'detailed'] as const,
    queryFn: () => teamService.fetchAssignedProjectsDetailed(),
  })

export const projectBriefsOptions = (projectId: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.assignedProjects, 'briefs', projectId] as const,
    queryFn: () => playbookService.fetchProjectBriefs(projectId!),
    enabled: !!projectId,
  })

export const playbookRevisionsOptions = (wpId: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'revisions', wpId] as const,
    queryFn: () => playbookService.fetchRevisions(wpId!),
    enabled: !!wpId,
    staleTime: 60_000,
  })

export const playbookFeedbackOptions = (wpId: string) =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'feedback', wpId] as const,
    queryFn: () => playbookService.fetchPlaybookFeedback(wpId),
    enabled: !!wpId,
  })

export const playbookOutputOptions = (wpId: string) =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'output', wpId] as const,
    queryFn: () => playbookService.fetchPlaybookOutput(wpId),
    enabled: !!wpId,
  })

export const archivedRunsOptions = (activeWpId: string | null | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.workspace, 'archived-runs', activeWpId] as const,
    queryFn: () => playbookService.fetchArchivedRuns(activeWpId!),
    enabled: !!activeWpId,
  })

// ─── Notification queries ───────────────────────────────────────
export const unreadCountOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.notifications, 'unread-count'] as const,
    queryFn: () => notificationService.fetchUnreadCount(),
    refetchInterval: NOTIFICATION_REFETCH_INTERVAL,
  })

export const notificationsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.notifications] as const,
    queryFn: () => notificationService.fetchNotifications(),
    refetchInterval: NOTIFICATION_REFETCH_INTERVAL,
  })

// ─── Message queries ────────────────────────────────────────────
export const contactsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.messages, 'contacts'] as const,
    queryFn: () => chatService.getContacts(),
  })

export const MESSAGES_PAGE_SIZE = 30

/**
 * Chat thread, paged backwards (most recent page first). Each page is a
 * chronological (oldest-first) slice; the cursor for the next, older page is
 * the oldest message's `created_at`. Capping the page bounds the server-side
 * reactions lookup that previously fanned out over an entire workspace.
 */
export const messagesInfiniteOptions = (contactId: string) =>
  infiniteQueryOptions({
    queryKey: [...queryKeys.messages, 'thread', contactId] as const,
    queryFn: ({ pageParam }) =>
      chatService.getMessages(contactId, { limit: MESSAGES_PAGE_SIZE, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length < MESSAGES_PAGE_SIZE ? undefined : lastPage[0]?.created_at,
    enabled: !!contactId,
  })

// ─── Profile queries ────────────────────────────────────────────
/** The whole brief row — callers that only want the answers read `.data`. */
export const businessBriefOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.profile, 'brief'] as const,
    queryFn: () => profileService.fetchBusinessBrief(),
  })

export const dueSurveysOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.surveys, 'due'] as const,
    queryFn: async () => (await surveyService.fetchDueSurveys()).surveys,
    staleTime: 5 * 60 * 1000, // weekly cadence; avoid refetch churn on home revisit/focus
  })

export const userProfileOptions = (userKey?: string | null) =>
  queryOptions({
    queryKey: [...queryKeys.profile, 'me', userKey ?? 'current'] as const,
    queryFn: () => profileService.fetchProfile(),
  })

export const myWorkspacesOptions = (userKey?: string | null) =>
  queryOptions({
    queryKey: [...queryKeys.profile, 'workspaces', userKey ?? 'current'] as const,
    queryFn: () => profileService.fetchMyWorkspaces(),
    staleTime: 5 * 60 * 1000,
  })

/**
 * Founder-relationship cards for the doer home page. User-scoped (returns every
 * founder the doer works with regardless of the active workspace), so it's
 * keyed under `profile` to survive workspace switches like myWorkspaces does.
 */
export const meFoundersOptions = (userKey?: string | null) =>
  queryOptions({
    queryKey: [...queryKeys.profile, 'founders', userKey ?? 'current'] as const,
    queryFn: async () => (await founderService.fetchFounderCards()).founders,
    staleTime: 60 * 1000,
  })

/**
 * The current agent installer for the /settings/activity-agent page.
 *
 * The response embeds a GCS URL signed for one hour. staleTime does not
 * refetch on its own — it only marks the data eligible, and the trigger is the
 * client's refetchOnWindowFocus. Five minutes means the doer who backgrounds
 * the tab and returns gets a fresh URL. It does not cover a tab held focused
 * for over an hour; guarding that properly needs the expiresAt the response
 * already carries, which nothing reads yet.
 *
 * retry: false because a 503 here means "nothing published yet" or "no
 * releases bucket configured", both normal states the page renders with their
 * own copy, not transient failures worth a second request.
 */
export const latestAgentReleaseOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.profile, 'agent-latest-release'] as const,
    queryFn: () => agentReleaseService.fetchLatestRelease(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

/**
 * The doer's own loyalty balance + ledger history for the home tile. User-scoped
 * (the balance sums across every workspace the doer earns in), so it's keyed
 * under `profile` to survive workspace switches like meFounders does.
 */
export const meLoyaltyOptions = (userKey?: string | null) =>
  queryOptions({
    queryKey: [...queryKeys.profile, 'loyalty', userKey ?? 'current'] as const,
    queryFn: () => loyaltyService.fetchSummary(),
    staleTime: 60 * 1000,
  })

// ─── Team queries ──────────────────────────────────────────────
export const userTeamsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.teams, 'user'] as const,
    queryFn: () => teamService.fetchTeamsForUser(),
  })

export const teamDetailOptions = (teamId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.teams, 'team', teamId] as const,
    queryFn: () => teamService.fetchTeamById(teamId!),
    enabled: !!teamId,
  })

export const teamMembersOptions = (teamId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.teams, 'membersForTeam', teamId] as const,
    queryFn: () => teamService.fetchTeamMembersForTeam(teamId!),
    enabled: !!teamId,
  })

// ─── Groups queries ─────────────────────────────────────────────
import type { GroupScope } from '@doneverse/shared'

export const groupsListOptions = (scope: GroupScope = 'mine') =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'list', scope] as const,
    queryFn: () => groupService.fetchGroups(scope),
  })

export const groupCollectionsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'collections'] as const,
    queryFn: () => groupService.fetchCollections(),
  })

export const collectionGroupsOptions = (collectionId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'collection-groups', collectionId] as const,
    queryFn: () => groupService.fetchCollectionGroups(collectionId!),
    enabled: !!collectionId,
  })

export const groupDetailOptions = (groupId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'detail', groupId] as const,
    queryFn: () => groupService.fetchGroupDetail(groupId!),
    enabled: !!groupId,
  })

export const groupMembersOptions = (groupId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'members', groupId] as const,
    queryFn: () => groupService.fetchMembers(groupId!),
    enabled: !!groupId,
  })

export const GROUP_POSTS_PAGE_SIZE = 20

export const groupPostsInfiniteOptions = (
  groupId: string | undefined,
  options: { includeDeleted?: boolean } = {},
) =>
  infiniteQueryOptions({
    queryKey: [...queryKeys.groups, 'posts', groupId, 'infinite', options.includeDeleted ?? false] as const,
    queryFn: ({ pageParam }) =>
      groupService.fetchGroupPosts(groupId!, {
        limit: GROUP_POSTS_PAGE_SIZE,
        before: pageParam,
        includeDeleted: options.includeDeleted,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length < GROUP_POSTS_PAGE_SIZE ? undefined : lastPage[lastPage.length - 1]?.created_at,
    enabled: !!groupId,
  })

export const GROUP_SEARCH_LIMIT = 50

export const groupPostsSearchOptions = (groupId: string | undefined, q: string) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'posts', groupId, 'search', q] as const,
    queryFn: () => groupService.fetchGroupPosts(groupId!, { q, limit: GROUP_SEARCH_LIMIT }),
    enabled: !!groupId && q.trim().length > 0,
  })

export const postCommentsOptions = (postId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'comments', postId] as const,
    queryFn: () => groupService.fetchPostComments(postId!),
    enabled: !!postId,
  })

export const mentionAutocompleteOptions = (groupId: string | undefined, q: string) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'mention-autocomplete', groupId, q] as const,
    queryFn: () => groupService.fetchMentionCandidates(groupId!, q),
    enabled: !!groupId,
    staleTime: 30_000,
  })

export const groupAutoJoinRulesOptions = (groupId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'auto-join-rules', groupId] as const,
    queryFn: () => groupService.fetchAutoJoinRules(groupId!),
    enabled: !!groupId,
  })

export const userMentionsOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'me', 'mentions'] as const,
    queryFn: () => groupService.fetchUserMentions(),
  })

export const userRepliesOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.groups, 'me', 'replies'] as const,
    queryFn: () => groupService.fetchUserReplies(),
  })

// ─── Invite queries ────────────────────────────────────────────
export const inviteLookupOptions = (inviteToken: string | undefined) =>
  queryOptions({
    queryKey: ['invite', inviteToken] as const,
    queryFn: () => authService.lookupInvite(inviteToken!),
    enabled: !!inviteToken,
    retry: false,
    staleTime: Infinity,
  })

// ─── File queries ──────────────────────────────────────────────
/** Prefix of every paged folder listing. */
export const fileListQueryKey = [...queryKeys.files, 'list-infinite'] as const

/** Prefix of every folder child count. */
export const folderChildCountQueryKey = [...queryKeys.files, 'child-count'] as const

/**
 * Every file LISTING — the paged folder contents and the child counts — and
 * nothing else.
 *
 * Invalidating the whole `['files']` prefix also matches every
 * `['files','detail',id]`, and a new file cannot make an existing file's detail
 * stale — but each of those refetches costs a signed URL. Named here so the
 * listing keys and the mutations that expire them cannot drift apart.
 */
export const fileListingQueryKeys = [fileListQueryKey, folderChildCountQueryKey] as const

/**
 * How many rows one page of a file listing asks for.
 *
 * Sized against the real data rather than a round number: the widest sibling
 * group in production is 157 rows and eight already exceed 50, so 100 keeps
 * every directory but the largest on a single request while still bounding
 * the read for whatever the Basecamp import grows next.
 */
export const FILES_PAGE_SIZE = 100

/** Folder browsers only ever render folders, so they page in smaller bites. */
export const FOLDER_PAGE_SIZE = 50

/**
 * One folder's contents, offset-paged. Each page carries the same `total`; we
 * advance by page number until the accumulated rows reach it — the same shape
 * as {@link taskListInfiniteOptions}.
 *
 * `kind` is part of the key because it changes which rows the pages contain: a
 * folder-only listing and a full listing are different sets, not different
 * views of one.
 */
export const fileListInfiniteOptions = (
  parentId?: string,
  options: { kind?: FileResponse['kind']; pageSize?: number } = {},
) => {
  const limit = options.pageSize ?? FILES_PAGE_SIZE
  return infiniteQueryOptions({
    queryKey: [...fileListQueryKey, parentId ?? 'root', options.kind ?? 'all', limit] as const,
    queryFn: ({ pageParam }) =>
      fileService.listFiles({ parentId, kind: options.kind, page: pageParam, limit }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.data.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
    staleTime: SIGNED_ASSET_STALE_TIME,
  })
}

/**
 * How many children a folder has, without reading them.
 *
 * `limit: 1` is the point: `total` counts every row matching the filters, not
 * the rows on the page, so one row off the wire answers "contains N items"
 * exactly. A caller that instead counted a page's length would understate a
 * folder wider than its limit — which in a destructive confirmation dialog is
 * the difference between warning about 50 items and deleting 157.
 */
export const folderChildCountOptions = (folderId: string | undefined) =>
  queryOptions({
    queryKey: [...folderChildCountQueryKey, folderId] as const,
    queryFn: async () => (await fileService.listFiles({ parentId: folderId, page: 1, limit: 1 })).total,
    enabled: !!folderId,
    // No staleTime: the number is the warning in a destructive dialog, and it
    // costs one row to re-read. The listings cache for signed-URL reasons that
    // do not apply to a count.
  })

export const fileDetailOptions = (fileId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.files, 'detail', fileId] as const,
    queryFn: () => fileService.getFile(fileId!),
    enabled: !!fileId,
    staleTime: SIGNED_ASSET_STALE_TIME,
  })

// ─── Label queries (Projects, Lists, Tags) ─────────────────────
/**
 * The workspace's labels of one kind. Small, bounded and unpaginated — a
 * workspace has a handful of Projects and Lists, not thousands.
 *
 * A list-grouped board needs this *before* it can query its sections, since
 * the labels are the sections. Route loaders prefetch it so that dependency
 * doesn't become a visible waterfall.
 */
export const labelListOptions = (kind?: LabelKind) =>
  queryOptions({
    queryKey: [...queryKeys.labels, 'list', kind ?? 'all'] as const,
    queryFn: () => labelService.listLabels(kind),
  })

/**
 * The same labels with their task counts, in one request rather than one query
 * per label. `collectionId` narrows to the labels used inside that project.
 */
export const labelCountsOptions = (kind: LabelKind, collectionId?: string) =>
  queryOptions({
    queryKey: [...queryKeys.labels, 'counts', kind, collectionId ?? 'all'] as const,
    queryFn: () => labelService.listLabelsWithCounts(kind, collectionId),
  })

// ─── Task queries ───────────────────────────────────────────────
export const taskListOptions = (filters: TaskListFilters = {}) =>
  queryOptions({
    queryKey: [...queryKeys.tasks, 'list', filters] as const,
    queryFn: () => taskService.listTasks(filters),
  })

export const TASKS_PAGE_SIZE = 30

/**
 * Offset-paged task list for infinite scroll. Each page carries the same
 * `total`; we advance by page number until the accumulated rows reach it.
 * `page`/`limit` are owned by the query — callers pass only the filters.
 */
export const taskListInfiniteOptions = (filters: Omit<TaskListFilters, 'page' | 'limit'> = {}) =>
  infiniteQueryOptions({
    queryKey: [...queryKeys.tasks, 'list-infinite', filters] as const,
    queryFn: ({ pageParam }) =>
      taskService.listTasks({ ...filters, page: pageParam, limit: TASKS_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.tasks.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
  })

export const taskDetailOptions = (taskId: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.tasks, 'detail', taskId] as const,
    queryFn: () => taskService.getTask(taskId!),
    enabled: !!taskId,
  })

// ─── Coaching call recordings queries ───────────────────────────
export const coachingCallRecordingsListOptions = (filters: CoachingCallRecordingListFilters = {}) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCallRecordings, 'list', filters] as const,
    queryFn: () => coachingCallRecordingsService.list(filters),
  })

export const coachingCallRecordingDetailOptions = (id: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCallRecordings, 'detail', id] as const,
    queryFn: () => coachingCallRecordingsService.get(id!),
    enabled: !!id,
  })

export const coachingCallRecordingMonthsOptions = (bucket?: CoachingRecordingBucket) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCallRecordings, 'months', bucket ?? 'all'] as const,
    queryFn: () => coachingCallRecordingsService.months(bucket),
  })

// ─── Doer's University queries ──────────────────────────────────
export const doersUniversityCatalogOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.doersUniversity, 'catalog'] as const,
    queryFn: () => doersUniversityService.catalog(),
    staleTime: SIGNED_ASSET_STALE_TIME, // static LearnWorlds export; avoid refetch churn on revisit/focus
  })

// ─── Masterclass Vault queries ──────────────────────────────────
export const masterclassVaultCatalogOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.masterclassVault, 'catalog'] as const,
    queryFn: () => masterclassVaultService.catalog(),
    staleTime: SIGNED_ASSET_STALE_TIME, // static LearnWorlds export; avoid refetch churn on revisit/focus
  })

export const masterclassVaultCourseOptions = (courseSlug: string) =>
  queryOptions({
    queryKey: [...queryKeys.masterclassVault, 'course', courseSlug] as const,
    queryFn: () => masterclassVaultService.course(courseSlug),
    staleTime: SIGNED_ASSET_STALE_TIME, // static LearnWorlds export; avoid refetch churn on revisit/focus
  })

// ─── Admin masterclass vault queries (full CRUD) ────────────────
export const adminMasterclassVaultListOptions = (slug?: string) =>
  queryOptions({
    queryKey: [...queryKeys.masterclassVault, 'admin', 'list', slug ?? 'masterclass-vault'] as const,
    queryFn: () => adminMasterclassVaultService.listCourses(slug),
  })

export const adminMasterclassVaultDetailOptions = (id: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.masterclassVault, 'admin', 'detail', id] as const,
    queryFn: () => adminMasterclassVaultService.getCourse(id!),
    enabled: !!id,
  })

// ─── Content views (per-user view tracking) queries ─────────────
// The list query is the one the dashboard reads (catalog progress, lesson
// nav checkmarks). The mutation is fired by the lesson page on first paint
// of each lesson; see useRecordContentView().
export const contentViewsListOptions = (input: { content_type: ContentViewKind; content_ids?: string[] }) =>
  queryOptions({
    queryKey: [...queryKeys.contentViews, input.content_type, input.content_ids ?? null] as const,
    queryFn: () => contentViewsService.list(input),
  })

export const recordContentViewMutationOptions = {
  mutationFn: (input: { content_type: ContentViewKind; content_id: string }) =>
    contentViewsService.record(input),
}

export const recordContentProgressMutationOptions = {
  mutationFn: (input: { content_type: ContentViewKind; content_id: string; percent: number }) =>
    contentViewsService.progress(input),
}

// ─── Founder Foundations (onboarding step 6) ────────────────────
// One course, one endpoint. The response already carries the founder's
// progress, so the page never joins content against content-views itself —
// unlocking and completion are decided server-side and must not be re-derived
// two different ways.
export const foundationsCourseOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.foundations, 'course'] as const,
    queryFn: () => foundationsService.course(),
  })

// ─── Founder's Lounge queries ───────────────────────────────────
export const foundersLoungeCatalogOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.foundersLounge, 'catalog'] as const,
    queryFn: () => foundersLoungeService.catalog(),
    staleTime: SIGNED_ASSET_STALE_TIME, // static LearnWorlds export; avoid refetch churn on revisit/focus
  })

export const foundersLoungeArticleOptions = (articleSlug: string) =>
  queryOptions({
    queryKey: [...queryKeys.foundersLounge, 'article', articleSlug] as const,
    queryFn: () => foundersLoungeService.article(articleSlug),
    staleTime: SIGNED_ASSET_STALE_TIME, // static LearnWorlds export; avoid refetch churn on revisit/focus
  })

// ─── Admin coaching call recordings queries ─────────────────────
export const adminCoachingCallRecordingsListOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.coachingCallRecordings, 'admin', 'list'] as const,
    queryFn: () => coachingCallRecordingsService.adminList(),
  })

export const adminCoachingCallRecordingDetailOptions = (id: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCallRecordings, 'admin', 'detail', id] as const,
    queryFn: () => coachingCallRecordingsService.adminGet(id!),
    enabled: !!id,
  })

// ─── Coaching calls (upcoming sessions) queries ────────────────
export const coachingCallsListOptions = (filters: ListCoachingCallsFilters = {}) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCalls, 'list', filters] as const,
    queryFn: () => coachingCallsService.list(filters),
  })

export const coachingCallDetailOptions = (id: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCalls, 'detail', id] as const,
    queryFn: () => coachingCallsService.get(id!),
    enabled: !!id,
  })

// ─── Admin coaching calls queries ──────────────────────────────
export const adminCoachingCallsListOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.coachingCalls, 'admin', 'list'] as const,
    queryFn: () => coachingCallsService.adminList(),
  })

export const adminCoachingCallDetailOptions = (id: string | undefined) =>
  queryOptions({
    queryKey: [...queryKeys.coachingCalls, 'admin', 'detail', id] as const,
    queryFn: () => coachingCallsService.adminGet(id!),
    enabled: !!id,
  })

// ─── Founder onboarding ────────────────────────────────────────
/**
 * The founder's service agreement for onboarding step 1 (ClickUp `86d3uknup`).
 *
 * A query over a POST, which the service explains: the endpoint creates the
 * DocuSeal submission on first use and is idempotent thereafter, so reading it
 * as a query is both accurate and one round trip cheaper than create-then-fetch.
 *
 * `staleTime: Infinity` because the answer is a document identity, not a status:
 * whether the founder has signed is settled by the Keap event arriving on
 * `/v1/me`, never by refetching this. Without it, every window focus while the
 * founder sits in the signing frame would re-POST and re-mount the iframe under
 * them, wiping half-entered signature fields.
 */
export const onboardingAgreementOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.onboarding, 'agreement'] as const,
    queryFn: () => onboardingService.agreement(),
    staleTime: Infinity,
    gcTime: Infinity,
  })

/**
 * Step 10's launch checklist (ClickUp `86d3ukp27`).
 *
 * Keyed like its neighbours — the workspace lives in the
 * `x-doneverse-workspace-id` header, and switching it resets every
 * workspace-scoped query (`resetWorkspaceScopedQueries`) rather than leaving
 * one founder's answers on screen under another's name.
 *
 * The read no longer closes step 10 for anybody — Launch Day's "Next" does that
 * (ClickUp `86d40tuc8`) — so this is a plain read of the founder's notes.
 */
export const launchChecklistOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.onboarding, 'launch-checklist'] as const,
    queryFn: () => launchChecklistService.fetch(),
  })

/**
 * Step 10's Launch Day — when the founder's first day with their Doer is, and
 * whether it has come (ClickUp `86d40tuc8`).
 *
 * Deliberately NOT `staleTime: Infinity`. `launchDateReached` flips at midnight
 * in the founder's own timezone, there is no cron to push it (the "no
 * effective-date scheduler" decision), and it is what enables the button that
 * finishes onboarding. So a refetch on focus is the mechanism by which a tab
 * left open overnight ever notices — and the Launch Day screen also refetches
 * the moment its countdown reaches zero.
 */
export const launchDayOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.onboarding, 'launch-day'] as const,
    queryFn: () => launchDayService.fetch(),
  })

/**
 * The signed-in doer's own profile, for the editor on /doer-profile.
 *
 * User-scoped, not workspace-scoped: a doer has ONE profile however many
 * founders they work with, so `resetWorkspaceScopedQueries` exempts this key by
 * name. It has to — that sweep is `resetQueries`, so without the exemption a
 * multi-founder doer switching workspace mid-edit would watch the editor drop
 * back to its skeleton.
 */
export const doerProfileOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.doerProfiles, 'me'] as const,
    queryFn: () => doerProfileService.fetchMine(),
  })

/**
 * The active workspace's doers, for the founder's stacked view — onboarding
 * step 9's page.
 *
 * Keyed like `launchChecklistOptions`: the workspace lives in the request
 * header, so the key carries no workspace id and a switch resets it with the
 * other workspace-scoped queries rather than showing one founder's doer under
 * another's name.
 */
export const workspaceDoerProfilesOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.doerProfiles, 'workspace'] as const,
    queryFn: () => doerProfileService.fetchWorkspaceDoers(),
  })
